import StorageManager from './StorageManager'
import { analytics, EVENT_NAMES } from './Analytics/Analytics'
import { updateAccountApi } from '../api/account.api'
import { callApi } from '../api/apiProvider'
import { logger } from './logger'
import * as userEmail from './userEmail'
import {
  getCountrySource,
  getIPAddress,
  getLocale,
  getState,
} from './countryService'
import { ServerSideAnalyticsPlatform } from './Analytics/ServerSideAnalytics'
import { updateFacebookAdvancedMatching } from './Analytics/platforms/facebook'
import { getUserPhoneNumber } from './User/phoneNumber'
import { userState } from './User/userState'
import { mixtilesAxios } from '../utils/ApiUtils'
import { getUserBrowserProperties } from '../utils/utils'
import { Subject } from 'rxjs'
import { experimentManager } from './ExperimentManager/ExperimentManager'
import { isServer } from 'utils/runtimeUtils'
import axios from 'axios'
import pick from 'lodash/pick'
import { getAmplitudeDeviceId } from './Analytics/platforms/amplitude'

const USER_FULL_NAME_KEY = 'userName'
const ACCOUNT_FULL_NAME_KEY = 'accountFullName'
const EXPLICIT_EMAIL_CONSENT_KEY = 'explicitEmailConsent'
const LOGGED_USER_KEYS = [
  'username',
  'unifiedUid',
  'isExistingUser',
  'deviceId',
]
export const CAPTURE_EMAIL_SOURCE = {
  login: 'Login',
  onboarding: 'Onboarding',
  checkoutAddress: 'Checkout Address',
  checkoutPayment: 'Checkout Payment',
  checkoutGiftCard: 'Checkout Gift Card',
  artOnboarding: 'Art Onboarding',
  artProductPage: 'Art Product Page',
  crmLead: 'CRM Lead',
  orderCompleted: 'Order Completed',
  switchBrowser: 'Switch Browser',
  footer: 'Footer',
  initialBrazeId: 'initial_braze_id',
}

const onEmailChange = new Subject()
const onUserChange = new Subject()

let initialUserParams

class UserManager {
  static init() {
    if (isServer()) return
    updateFacebookAdvancedMatching({
      email: UserManager.getUserEmail(),
      phoneNumber: getUserPhoneNumber(),
    })
  }

  static setIsNewEmail(isNewEmail) {
    // This actually indicates if this email is new in the current session
    // and will be used for the create new order analytics
    this._isNewEmail = isNewEmail

    const email = this.getUserEmail()
    if (isNewEmail && email) {
      analytics.track(
        'New Email Captured',
        { Email: email },
        { serverSideAnalyticsPlatforms: [ServerSideAnalyticsPlatform.Facebook] }
      )
    }
  }

  static isNewEmail() {
    return this._isNewEmail
  }

  static getUser() {
    return userState.getUser()
  }

  static subscribeToExperimentFetch(callback) {
    return onEmailChange.subscribe(callback)
  }

  static subscribeToUserChange(callback) {
    return onUserChange.subscribe(callback)
  }

  static clearData() {
    userEmail.clearUserEmail()
    StorageManager.remove(USER_FULL_NAME_KEY)
    UserManager.clearAccountFullNameSynced()
  }

  static hasExplicitEmailConsent(email) {
    const consent = StorageManager.get(EXPLICIT_EMAIL_CONSENT_KEY)
    return consent?.email === email.toLowerCase() && consent?.hasExplicitConsent
  }

  static setExplicitEmailConsent(email, hasExplicitConsent) {
    if (email) {
      StorageManager.set(EXPLICIT_EMAIL_CONSENT_KEY, {
        email: email.toLowerCase(),
        hasExplicitConsent,
      })
    }
  }

  static initializeUserProperties() {
    const { countryCode, country } = getLocale()
    analytics.setCountryCode(countryCode)
    analytics.setUserProperties({
      'User Country': country,
      'User Country State': getState(),
      'User Country Source': getCountrySource(),
      'User IP Address': getIPAddress(),
      ...getUserBrowserProperties(),
    })
  }

  static onUUidSet(unifiedUidToUse, isExistingUser) {
    const localDataToSet = {
      ...userState.getUser(),
      isExistingUser,
      unifiedUid: unifiedUidToUse,
    }
    userState.setUser(localDataToSet)

    onUserChange.next(localDataToSet)
  }

  static async setUserEmail({
    email,
    source,
    onboardingFlow,
    refetchExperiments = true,
    emailConsent = false,
  }) {
    const previousEmail = UserManager.getUserEmail()
    const currUser = userState.getUser()
    if (!email) {
      return
    }

    const loggedCurrUser = pick(currUser, LOGGED_USER_KEYS)
    logger.info('setUserEmail func triggered', {
      email,
      previousEmail,
      currUser: loggedCurrUser,
    })

    analytics.track(EVENT_NAMES.SET_USER_EMAIL_TRIGGERED, {
      Email: email,
      previousEmail,
      currUser: loggedCurrUser,
    })

    // in case we never set the unified Uid before and we still have the same email to be set as the local storage
    // we want to force the update of the unifiedUid
    if (email === previousEmail && currUser.unifiedUid) {
      return
    } else if (email !== previousEmail && previousEmail) {
      analytics.track(EVENT_NAMES.EMAIL_CAPTURED_OVERRIDDEN, {
        Email: email,
        previousEmail,
        currUser: loggedCurrUser,
      })
    }

    email = email.toLowerCase()

    let response
    userEmail.setUserEmail(email)
    const amplitudeDeviceId = getAmplitudeDeviceId()
    try {
      response = await mixtilesAxios.post('v1/user/userCapturedData', {
        email,
        amplitudeDeviceId,
      })
      const isExistingUser = !response.data.isNewEmail
      logger.info('captured user email', {
        ...response.data,
        currUser: loggedCurrUser,
      })
      let unifiedUidToUse
      if (response.data.firstUnifiedUid) {
        unifiedUidToUse = response.data.firstUnifiedUid
        if (!currUser.unifiedUid) {
          logger.info(
            'current user does not have unifiedUid, updating it with firstUnifiedUid',
            { currUser: loggedCurrUser, unifiedUidToUse }
          )
          await mixtilesAxios.post('v1/user/updateUnifiedUid', {
            email,
            unifiedUid: unifiedUidToUse,
          })
          UserManager.onUUidSet(unifiedUidToUse, isExistingUser)
        }

        if (refetchExperiments) {
          await experimentManager.fetch(unifiedUidToUse)
          analytics.track(EVENT_NAMES.STICKY_EXPERIMENTS_APPLIED, {
            'First Username': response.data.firstUsername,
            'Unified UID': unifiedUidToUse,
            'Previous Email': previousEmail,
            'New Email': email,
          })
        }
        analytics.setUserProperties({
          'Initial User ID': response.data.firstUsername,
          'Unified UID': unifiedUidToUse,
        })
      } else {
        const user = UserManager.getUser()
        unifiedUidToUse = user.unifiedUid || user.suggestedUnifiedUid
        await mixtilesAxios.post('v1/user/updateUnifiedUid', {
          email,
          unifiedUid: unifiedUidToUse,
        })
        logger.info(
          'User is new, created unifiedUid and updated it in user object',
          {
            currUser: pick(user, LOGGED_USER_KEYS),
            createdUnifiedUid: unifiedUidToUse,
          }
        )
        UserManager.onUUidSet(unifiedUidToUse, isExistingUser)
      }
      const user = UserManager.getUser()
      await axios.post('/set-session', { unifiedUid: unifiedUidToUse })
      logger.info('Set unifiedUid in session', {
        currUser: pick(user, LOGGED_USER_KEYS),
        unifiedUidToUse,
      })
      UserManager.setIsNewEmail(response.data.isNewEmail)

      await analytics.identifyUser(user.unifiedUid, email, emailConsent)

      analytics.setUserEmail({
        email,
        trackEventName: EVENT_NAMES.WEB_EMAIL_CAPTURED,
        isNewEmail: UserManager.isNewEmail(),
        trackProperties: {
          Source: source,
          'First Username': response.data.firstUsername,
          'Onboarding Flow': onboardingFlow,
        },
      })
      onEmailChange.next(email)
    } catch (e) {
      logger.error('Failed to capture user email', e)
    }
  }

  /**
   * @returns {string}
   */
  static getUserEmail() {
    return userEmail.getUserEmail()
  }

  static setUserFullName(name, persist = true) {
    StorageManager.set(USER_FULL_NAME_KEY, name)
    analytics.setUserFullName(name)
    if (persist && name !== StorageManager.get(ACCOUNT_FULL_NAME_KEY)) {
      callApi(updateAccountApi({ fullName: name }))
        .then((account) => {
          // Resolves empty when signed out, so the name still reaches the account after login
          if (account) {
            UserManager.markAccountFullNameSynced(account.fullName)
          }
        })
        .catch((error) => {
          logger.error('Failed to update account full name', error)
        })
    }
  }

  static markAccountFullNameSynced(fullName) {
    StorageManager.set(ACCOUNT_FULL_NAME_KEY, fullName)
  }

  static clearAccountFullNameSynced() {
    StorageManager.remove(ACCOUNT_FULL_NAME_KEY)
  }

  static getUserFullName() {
    return StorageManager.get(USER_FULL_NAME_KEY)
  }

  static getUserSessionToken() {
    return UserManager.getUser().sessionToken
  }

  static getUserAttributions = async () => {
    try {
      const { data } = await mixtilesAxios.get('/v4/userAttributions')
      return data || {}
    } catch (error) {
      logger.error('Failed to fetch user attributions from server', error)
      return {}
    }
  }

  static getInitialUserParams = async () => {
    if (!initialUserParams) {
      initialUserParams = (await UserManager.getUserAttributions())
        .initialParams
    }
    return initialUserParams || {}
  }
}

UserManager.init() // TODO: reconsider

export default UserManager
